import { createReducer } from "@reduxjs/toolkit";
import {
  loginRequest,
  loginSuccess,
  loginFailure,
  logout,
  registerRequest,
  registerSuccess,
  registerFailure,
  checkAuthRequest,
  checkAuthSuccess,
  checkAuthFailure,
  rehydrateAuth,
  profileRequest,
  profileSuccess,
  profileFailure,
  userStatsRequest,
  userStatsSuccess,
  userStatsFailure,
  updateUserVipStatus,
} from "./authActions";
import { RootState } from "..";
import { UserProfile, UserStatistics } from "../types";
import LS from "../../utils/localStorageUtils";
import { isTokenExpired } from "../../utils/helpers/tokenUtils";

export type AuthState = {
  token?: string;
  user?: UserProfile;
  loading?: boolean;
  profileLoading?: boolean;
  error?: string;
  profileError?: string;
  isLogged?: boolean;
  statistics?: UserStatistics;
  statisticsLoading?: boolean;
  statisticsError?: string;
};

export const initialState: AuthState = {
  loading: false,
  isLogged: false,
  profileLoading: false,
  token: undefined,
  user: undefined,
  error: undefined,
  profileError: undefined,
  statistics: undefined,
  statisticsLoading: undefined,
  statisticsError: undefined,
};

export const authReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(loginRequest, (state) => {
      state.loading = true;
      state.error = undefined;
    })
    .addCase(loginSuccess, (state, action) => {
      console.log("[AuthReducer] loginSuccess", action.payload);
      state.loading = false;
      state.token = action.payload.token;
      // state.user = action.payload.user;
      // Set isLogged based on token validity (matching checkAuthSuccess behavior)
      const tokenValid =
        Boolean(action.payload.token) && !isTokenExpired(action.payload.token);
      state.isLogged = tokenValid;
      console.log("[AuthReducer] Token set:", !!action.payload.token);
      console.log(
        "[AuthReducer] Token expired check:",
        isTokenExpired(action.payload.token),
      );
      console.log("[AuthReducer] isLogged set to:", tokenValid);
    })
    .addCase(loginFailure, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.isLogged = false;
    })
    .addCase(logout, (state) => {
      state.token = undefined;
      state.user = undefined;
      state.isLogged = false;
    })
    .addCase(registerRequest, (state) => {
      state.loading = true;
      state.error = undefined;
    })
    .addCase(registerSuccess, (state, action) => {
      state.loading = false;
    })
    .addCase(checkAuthRequest, (state) => {
      state.loading = true;
    })
    .addCase(checkAuthSuccess, (state, action) => {
      console.log("checkAuthSuccess", action.payload);
      state.loading = false;
      state.token = action.payload.token;
      // state.user = action.payload.user;
      // Set isLogged based on token validity
      const tokenValid =
        Boolean(action.payload.token) && !isTokenExpired(action.payload.token);
      state.isLogged = tokenValid;
      console.log(
        "[AuthReducer] checkAuthSuccess - isLogged set to:",
        tokenValid,
      );
    })
    .addCase(checkAuthFailure, (state) => {
      state.loading = false;
      state.token = undefined;
      state.user = undefined;
      state.isLogged = false;
    })
    .addCase(profileRequest, (state) => {
      state.profileLoading = true;
      state.profileError = undefined;
    })
    .addCase(profileSuccess, (state, action) => {
      console.log(
        "[AuthReducer] ✅ profileSuccess reducer hit",
        action.payload?.username,
      );
      state.profileLoading = false;
      state.user = action.payload;
    })
    .addCase(profileFailure, (state, action) => {
      state.profileLoading = false;
      state.profileError = action.payload;
    })
    .addCase(registerFailure, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })
    .addCase(userStatsRequest, (state) => {
      state.statisticsLoading = true;
      state.statisticsError = undefined;
    })
    .addCase(userStatsSuccess, (state, action) => {
      state.statisticsLoading = false;
      state.statistics = action.payload;
    })
    .addCase(userStatsFailure, (state, action) => {
      state.statisticsLoading = false;
      state.statisticsError = action.payload;
    })
    .addCase(rehydrateAuth, (state, action) => {
      state.token = action.payload.token;
      // state.isLogged = Boolean(action.payload?.token);
      state.user = action.payload.user;
      const storedProfile = LS.get("auth_user");
      if (storedProfile) {
        try {
          state.user = JSON.parse(storedProfile);
        } catch (e) {
          console.error("Failed to parse stored user data", e);
        }
      }
    })
    .addCase(updateUserVipStatus, (state, action) => {
      if (state.user) {
        state.user = {
          ...state.user,
          isVip: action.payload.isVip,
        };

        try {
          LS.set("auth_user", JSON.stringify(state.user));
        } catch (e) {
          console.error(
            "Failed to save updated user VIP status to localStorage",
            e,
          );
        }
      }
    });
});

export const selectIsLogged = (s: RootState) => {
  const token = s.auth.token;
  const hasToken = Boolean(token);

  // If there's a token, check if it's expired
  // But be lenient - if token exists and state says we're logged in, trust it
  // This handles the case where token validation might fail due to timing issues
  if (!hasToken) {
    return false;
  }

  // Check if token is expired
  const notExpired = !isTokenExpired(token);

  // Also check the state.isLogged flag as a fallback
  // This ensures that if loginSuccess set isLogged=true, we trust it
  const stateSaysLogged = s.auth.isLogged === true;

  // User is logged in if: token exists AND (not expired OR state says logged in)
  // This handles race conditions where token validation might fail temporarily
  const result = hasToken && (notExpired || stateSaysLogged);

  // Debug logging
  console.log("[selectIsLogged] Token check:", {
    hasToken,
    notExpired,
    stateSaysLogged,
    result,
    tokenPreview: token ? token.substring(0, 20) + "..." : "no token",
  });

  return result;
};
export const selectProfile = (s: RootState) => s.auth.user;
export const selectProfileLoading = (s: RootState) => s.auth.profileLoading;
export const selectProfileError = (s: RootState) => s.auth.profileError;
export const selectStats = (s: RootState) => s.auth.statistics;
export const selectStatsLoading = (s: RootState) => s.auth.statisticsLoading;
